import { Button, Dialog, Field, TextArea } from "@hisani/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import React, { useEffect, useRef, useState, type FormEvent } from "react";
import { ApiClientError, requestApiJson } from "../../api-client";
import {
  MessageSchema,
  type MarketplaceConversation,
} from "./marketplace-contracts";
import {
  marketplaceQueryKeys,
  type MarketplaceConversationData,
} from "./marketplace-queries";

export function ConversationDialog({
  open,
  title,
  participantContext,
  conversation,
  accountId,
  loading,
  conversationError,
  hasOlderMessages,
  isLoadingOlderMessages,
  olderMessagesFailed,
  onLoadOlderMessages,
  onRetry,
  onClose,
}: {
  readonly open: boolean;
  readonly title: string | null;
  readonly participantContext: string | null;
  readonly conversation: MarketplaceConversation | null;
  readonly accountId: string | undefined;
  readonly loading: boolean;
  readonly conversationError: boolean;
  readonly hasOlderMessages: boolean;
  readonly isLoadingOlderMessages: boolean;
  readonly olderMessagesFailed: boolean;
  readonly onLoadOlderMessages: () => void;
  readonly onRetry: () => void;
  readonly onClose: () => void;
}) {
  const t = useTranslations("Marketplace");
  const locale = useLocale();
  const queryClient = useQueryClient();
  const pendingMessage = useRef<PendingMessage | null>(null);
  const historyEndRef = useRef<HTMLLIElement>(null);
  const [submissionRequiresReplay, setSubmissionRequiresReplay] =
    useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const messageMutation = useMutation({
    mutationFn: ({ conversationId, requestBody }: SendMessageVariables) =>
      requestApiJson(
        `/api/v1/marketplace/conversations/${conversationId}/messages`,
        MessageSchema,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: requestBody,
        },
      ),
    onSuccess: (message, { conversationId, formElement }) => {
      pendingMessage.current = null;
      setSubmissionRequiresReplay(false);
      setValidationError(null);
      queryClient.setQueryData<MarketplaceConversationData>(
        marketplaceQueryKeys.conversation(conversationId),
        (current) => {
          const latest = current?.pages[0];
          if (
            current === undefined ||
            latest === undefined ||
            current.pages.some((page) =>
              page.messages.some(({ id }) => id === message.id),
            )
          ) {
            return current;
          }
          return {
            ...current,
            pages: [
              { ...latest, messages: [...latest.messages, message] },
              ...current.pages.slice(1),
            ],
          };
        },
      );
      formElement.reset();
      formElement.querySelector<HTMLTextAreaElement>("textarea")?.blur();
      void queryClient.invalidateQueries({
        queryKey: marketplaceQueryKeys.conversations,
      });
    },
    onError: (error) => {
      if (isDefinitiveMessageRejection(error)) {
        pendingMessage.current = null;
        setSubmissionRequiresReplay(false);
        return;
      }
      setSubmissionRequiresReplay(true);
    },
  });
  const pending = messageMutation.isPending;
  const composerError =
    validationError ??
    (messageMutation.error === null
      ? null
      : submissionRequiresReplay
        ? t("messageRetryNotice")
        : t("messageError"));
  const latestMessageId = conversation?.messages.at(-1)?.id;

  useEffect(() => {
    if (latestMessageId === undefined) return;
    const scrollContainer = historyEndRef.current?.closest<HTMLElement>(
      '[data-slot="dialog-body"]',
    );
    scrollContainer?.scrollTo?.({ top: scrollContainer.scrollHeight });
  }, [latestMessageId]);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (conversation === null || messageMutation.isPending) return;
    const formElement = event.currentTarget;
    let submission = pendingMessage.current;
    if (submission === null) {
      const value = new FormData(formElement).get("message");
      const body = typeof value === "string" ? value.trim() : "";
      if (body === "") {
        setValidationError(t("messageRequired"));
        return;
      }
      setValidationError(null);
      submission = {
        conversationId: conversation.id,
        requestBody: JSON.stringify({
          body,
          idempotencyKey: crypto.randomUUID(),
        }),
      };
      pendingMessage.current = submission;
    }
    messageMutation.mutate({
      ...submission,
      formElement,
    });
  }

  function closeDialog() {
    pendingMessage.current = null;
    setSubmissionRequiresReplay(false);
    setValidationError(null);
    messageMutation.reset();
    onClose();
  }

  return (
    <Dialog
      open={open}
      title={conversation?.listingHorseName ?? title ?? t("conversationTitle")}
      description={participantContext ?? t("conversationTitle")}
      closeLabel={t("close")}
      closeDisabled={pending}
      mobileFullscreen
      onClose={closeDialog}
      footer={
        conversation === null && loading ? (
          <div
            className="grid w-full grid-cols-[minmax(0,1fr)_64px] gap-2"
            aria-hidden="true"
          >
            <div className="h-12 animate-pulse rounded-xl bg-subtle" />
            <div className="h-12 animate-pulse rounded-xl bg-brand-subtle" />
          </div>
        ) : conversation === null ? undefined : (
          <form
            onSubmit={submit}
            className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-end gap-2"
          >
            <Field
              label={t("messageLabel")}
              error={composerError ?? undefined}
              className="min-w-0 [&>label]:sr-only"
            >
              {(field) => (
                <TextArea
                  {...field}
                  name="message"
                  aria-required="true"
                  maxLength={2000}
                  rows={1}
                  disabled={pending || submissionRequiresReplay}
                  onChange={() => setValidationError(null)}
                  className="max-h-[7.5rem] min-h-12 resize-none rounded-xl bg-subtle"
                />
              )}
            </Field>
            <Button
              type="submit"
              variant="accent"
              disabled={pending}
              className="min-h-12 rounded-xl px-4"
            >
              {pending
                ? t("sending")
                : submissionRequiresReplay
                  ? t("retryMessage")
                  : t("sendMessage")}
            </Button>
          </form>
        )
      }
    >
      {loading ? (
        <div
          role="status"
          aria-label={t("conversationLoading")}
          className="grid gap-3 py-2"
        >
          <div className="flex gap-2 border-b border-border-subtle pb-3">
            <ShieldCheck
              className="mt-0.5 size-4 shrink-0 text-secondary"
              aria-hidden="true"
            />
            <p className="text-body-small text-secondary">
              {t("conversationSafety")}
            </p>
          </div>
          <div className="h-9 w-3/4 animate-pulse justify-self-start rounded-2xl bg-subtle" />
          <div className="h-14 w-4/5 animate-pulse justify-self-end rounded-2xl bg-brand-subtle/60" />
          <div className="h-12 w-2/3 animate-pulse justify-self-start rounded-2xl bg-subtle" />
          <span className="sr-only">{t("conversationLoading")}</span>
        </div>
      ) : conversationError ? (
        <div role="alert" className="border-y border-border-subtle py-4">
          <p className="text-sm font-semibold text-danger">
            {t("conversationError")}
          </p>
          <Button
            type="button"
            variant="secondary"
            className="mt-3 min-h-12"
            onClick={onRetry}
          >
            {t("retry")}
          </Button>
        </div>
      ) : conversation === null ? null : (
        <div className="grid content-start gap-3">
          <div className="flex gap-2 border-b border-border-subtle pb-3">
            <ShieldCheck
              className="mt-0.5 size-4 shrink-0 text-secondary"
              aria-hidden="true"
            />
            <p className="text-body-small text-secondary">
              {t("conversationSafety")}
            </p>
          </div>
          <ol
            role="log"
            aria-live="polite"
            aria-relevant="additions"
            aria-label={t("messageHistory")}
            className="grid gap-2.5 pb-2"
          >
            {olderMessagesFailed ? (
              <li
                role="alert"
                className="flex flex-wrap items-center justify-center gap-2 text-sm text-danger"
              >
                <span>{t("olderMessagesError")}</span>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onLoadOlderMessages}
                >
                  {t("retry")}
                </Button>
              </li>
            ) : hasOlderMessages ? (
              <li className="flex justify-center">
                <Button
                  type="button"
                  variant="ghost"
                  disabled={isLoadingOlderMessages}
                  onClick={onLoadOlderMessages}
                >
                  {isLoadingOlderMessages
                    ? t("loadingOlderMessages")
                    : t("loadOlderMessages")}
                </Button>
              </li>
            ) : null}
            {conversation.messages.length === 0 ? (
              <li className="py-6 text-center text-sm text-secondary">
                {t("noMessages")}
              </li>
            ) : (
              conversation.messages.map((message, index) => {
                const ownMessage = message.senderId === accountId;
                const startsGroup =
                  conversation.messages[index - 1]?.senderId !==
                  message.senderId;
                const endsGroup =
                  conversation.messages[index + 1]?.senderId !==
                  message.senderId;
                const senderLabel = ownMessage
                  ? t("you")
                  : conversation.sellerId === accountId
                    ? t("buyer")
                    : t("seller");
                return (
                  <li
                    key={message.id}
                    className={`max-w-[78%] rounded-2xl px-3.5 py-2.5 ${
                      ownMessage
                        ? `${endsGroup ? "rounded-ee-sm" : ""} justify-self-end bg-brand-subtle text-brand-ink`
                        : `${endsGroup ? "rounded-es-sm" : ""} justify-self-start bg-subtle text-primary`
                    }`}
                  >
                    {startsGroup ? (
                      <span className="text-body-small font-semibold">
                        {senderLabel}
                      </span>
                    ) : null}
                    <p
                      dir="auto"
                      className={startsGroup ? "text-body mt-1" : "text-body"}
                    >
                      {message.body}
                    </p>
                    {endsGroup ? (
                      <time
                        dateTime={message.createdAt}
                        className="text-body-small mt-1 block text-tertiary"
                      >
                        {new Intl.DateTimeFormat(locale, {
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        }).format(new Date(message.createdAt))}
                      </time>
                    ) : null}
                  </li>
                );
              })
            )}
            <li ref={historyEndRef} className="h-0" aria-hidden="true" />
          </ol>
        </div>
      )}
    </Dialog>
  );
}

interface SendMessageVariables {
  readonly conversationId: string;
  readonly requestBody: string;
  readonly formElement: HTMLFormElement;
}

interface PendingMessage {
  readonly conversationId: string;
  readonly requestBody: string;
}

function isDefinitiveMessageRejection(error: unknown): boolean {
  return (
    error instanceof ApiClientError &&
    error.status >= 400 &&
    error.status < 500 &&
    error.status !== 408 &&
    error.status !== 429
  );
}
